import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Users, UserPlus, UserMinus, ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";

const computeAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

function AddMemberDialog({ onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", relation: "", dob: "", reason: "" });
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/household-change-requests", {
        action: "add",
        member: { name: form.name, relation: form.relation, dob: form.dob },
        reason: form.reason,
      });
      toast.success("Request submitted — pending admin approval");
      setOpen(false);
      setForm({ name: "", relation: "", dob: "", reason: "" });
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-add-member-request">
          <UserPlus className="w-4 h-4 mr-2" /> Request to Add Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request to Add Household Member</DialogTitle>
          <DialogDescription>
            This submits a change request to the admin for review. Your live household roster
            will be updated only after the admin approves.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-member-name" /></div>
          <div><Label>Relationship</Label><Input value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} placeholder="Spouse, Child, Roommate…" data-testid="add-member-relation" /></div>
          <div><Label>Date of Birth</Label><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="add-member-dob" /></div>
          <div><Label>Reason (optional)</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why is this person joining the household?" data-testid="add-member-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="submit-add-member">Submit Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemoveMemberDialog({ member, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await api.post("/household-change-requests", {
        action: "remove",
        member,
        reason,
      });
      toast.success("Removal request submitted — pending admin approval");
      setOpen(false);
      setReason("");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`open-remove-${member.id}`}>
          <UserMinus className="w-4 h-4 text-red-600" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request to Remove {member.name}?</DialogTitle>
          <DialogDescription>
            This submits a change request to the admin for review. {member.name} will remain on
            your live roster until the admin approves the removal.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Reason for removal</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., Moved out, relationship change, etc." data-testid="remove-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} variant="destructive" data-testid="submit-remove">Submit Removal Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmt(dt) { try { return new Date(dt).toLocaleString(); } catch { return dt; } }

export default function ProviderHousehold() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    try {
      const [{ data: meData }, { data: reqs }] = await Promise.all([
        api.get("/auth/me"),
        api.get("/household-change-requests/mine"),
      ]);
      setMe(meData);
      setRequests(reqs);
    } catch (_e) { /* */ }
  };
  useEffect(() => { load(); }, []);

  if (user?.role === "respite") return <p className="text-sm text-muted-foreground">Respite providers cannot manage household members.</p>;
  if (!me) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const household = me.household_members || [];

  return (
    <div className="space-y-4" data-testid="provider-household">
      <Button variant="ghost" size="sm" onClick={() => navigate("/provider")} data-testid="back-home"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div>
        <p className="text-label">My Profile</p>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Household Members</h1>
        <p className="text-sm text-muted-foreground">
          Any add or remove submitted here creates a Change Request that the admin must approve before your live profile updates.
        </p>
      </div>

      <Card className="p-4 space-y-3" data-testid="household-roster">
        <div className="flex items-center justify-between">
          <p className="text-label">Current household ({household.length})</p>
          <AddMemberDialog onSubmitted={load} />
        </div>
        {household.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No household members on file.</p>}
        {household.map((m) => {
          const age = computeAge(m.dob);
          return (
            <div key={m.id} className="border border-border rounded-md p-3 flex items-center justify-between gap-2" data-testid={`hh-${m.id}`}>
              <div className="min-w-0">
                <p className="font-display font-semibold text-sm">{m.name}</p>
                <p className="text-xs text-muted-foreground">
                  {m.relation || "Household Member"}{age != null ? ` · ${age} yrs` : ""}{m.dob ? ` · DOB ${m.dob}` : ""}
                </p>
              </div>
              <RemoveMemberDialog member={m} onSubmitted={load} />
            </div>
          );
        })}
      </Card>

      <Card className="p-4 space-y-2" data-testid="my-requests">
        <p className="text-label">My change requests</p>
        {requests.length === 0 && <p className="text-sm text-muted-foreground">You haven't submitted any requests.</p>}
        {requests.map((r) => (
          <div key={r.id} className="border border-border rounded-md p-3 flex items-start justify-between gap-2 text-sm" data-testid={`my-req-${r.id}`}>
            <div className="min-w-0">
              <p>
                <strong>{r.action === "add" ? "Add" : "Remove"}</strong>{" "}
                <span className="font-display font-semibold">{r.member?.name}</span>
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(r.created_at)}</p>
              {r.decision_notes && r.status !== "pending" && (
                <p className="text-xs italic mt-1">Admin: "{r.decision_notes}"</p>
              )}
            </div>
            <Badge variant={r.status === "pending" ? "secondary" : r.status === "approved" ? "default" : "destructive"} data-testid={`my-req-status-${r.id}`}>{r.status}</Badge>
          </div>
        ))}
      </Card>
    </div>
  );
}
